import React, { useState, useEffect } from 'react';
import { useBrand } from '../../contexts/BrandContext';
import { Make_Authorized_API_Call, isSuccessResponse } from '../../api/apiClient';
import { API_ENDPOINTS } from '../../services/api/endpoints';

// Note: VelocityMind metrics endpoint pending backend implementation in template-core.yaml

interface SprintMetrics {
  currentSprint: string;
  velocity: number;
  velocityTrend: number;
  completionRate: number;
  storyPointsCompleted: number;
  storyPointsRemaining: number;
  sprintDaysRemaining: number;
}

interface TeamHealthMetrics {
  overallHealth: 'healthy' | 'warning' | 'critical';
  burnoutRisk: number;
  collaborationScore: number;
  codeReviewTurnaround: number;
}

interface DORAMetrics {
  deploymentFrequency: number;
  leadTimeForChanges: number;
  changeFailureRate: number;
  mttr: number;
}

const VelocityMindDashboard: React.FC = () => {
  const { primaryColor, brand } = useBrand();
  const brandColor = brand?.Primary_Color || primaryColor || '#EF4444';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sprintMetrics, setSprintMetrics] = useState<SprintMetrics>({
    currentSprint: '',
    velocity: 0,
    velocityTrend: 0,
    completionRate: 0,
    storyPointsCompleted: 0,
    storyPointsRemaining: 0,
    sprintDaysRemaining: 0
  });

  const [teamHealth, setTeamHealth] = useState<TeamHealthMetrics>({
    overallHealth: 'healthy',
    burnoutRisk: 0,
    collaborationScore: 0,
    codeReviewTurnaround: 0
  });

  const [doraMetrics, setDORAMetrics] = useState<DORAMetrics>({
    deploymentFrequency: 0,
    leadTimeForChanges: 0,
    changeFailureRate: 0,
    mttr: 0
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch velocity metrics from API
        const response = await Make_Authorized_API_Call<{
          sprint: SprintMetrics;
          teamHealth: TeamHealthMetrics;
          dora: DORAMetrics;
        }>(API_ENDPOINTS.VELOCITYMIND.METRICS, 'GET');

        if (isSuccessResponse(response)) {
          const metrics = response.data.Records[0];

          if (metrics) {
            setSprintMetrics(metrics.sprint || sprintMetrics);
            setTeamHealth(metrics.teamHealth || teamHealth);
            setDORAMetrics(metrics.dora || doraMetrics);
          }
        } else {
          throw new Error(response.message || 'Failed to load metrics');
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
        console.error('[VelocityMindDashboard] Error:', errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const renderMetric = (value: number | string, loading: boolean) => {
    if (loading) return <span className="text-gray-400">--</span>;
    return value;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Engineering Analytics</h1>
        <p className="text-gray-600">VelocityMind Dashboard</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold">Error loading dashboard</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Sprint Metrics */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sprint Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Current Velocity</p>
            <p className="text-2xl font-bold" style={{ color: brandColor }}>
              {renderMetric(sprintMetrics.velocity, loading)}
            </p>
            {!loading && sprintMetrics.velocityTrend !== 0 && (
              <p className={`text-sm ${sprintMetrics.velocityTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {sprintMetrics.velocityTrend > 0 ? '+' : ''}{sprintMetrics.velocityTrend}% vs last sprint
              </p>
            )}
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Completion Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(`${sprintMetrics.completionRate}%`, loading)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Story Points Completed</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(sprintMetrics.storyPointsCompleted, loading)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Days Remaining</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(sprintMetrics.sprintDaysRemaining, loading)}
            </p>
          </div>
        </div>
      </div>

      {/* Team Health */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Overall Health</p>
            <p className={`text-2xl font-bold capitalize ${getHealthColor(teamHealth.overallHealth)}`}>
              {renderMetric(teamHealth.overallHealth, loading)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Burnout Risk</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(`${teamHealth.burnoutRisk}%`, loading)}
            </p>
            {!loading && teamHealth.burnoutRisk > 60 && (
              <p className="text-sm text-yellow-600">Monitor closely</p>
            )}
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Collaboration Score</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(teamHealth.collaborationScore, loading)}/100
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">PR Review Time</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(`${teamHealth.codeReviewTurnaround}h`, loading)}
            </p>
          </div>
        </div>
      </div>

      {/* DORA Metrics */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">DORA Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Deployment Frequency</p>
            <p className="text-2xl font-bold" style={{ color: brandColor }}>
              {renderMetric(doraMetrics.deploymentFrequency, loading)}
            </p>
            <p className="text-sm text-gray-500">deploys/day</p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Lead Time for Changes</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(doraMetrics.leadTimeForChanges, loading)}
            </p>
            <p className="text-sm text-gray-500">hours</p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">Change Failure Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(`${doraMetrics.changeFailureRate}%`, loading)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-sm text-gray-500">MTTR</p>
            <p className="text-2xl font-bold text-gray-900">
              {renderMetric(doraMetrics.mttr, loading)}
            </p>
            <p className="text-sm text-gray-500">minutes</p>
          </div>
        </div>
      </div>

      {/* Velocity Chart Placeholder */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Velocity Trend</h2>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
          <p className="text-gray-500">
            {loading ? 'Loading chart data...' : 'Velocity chart visualization'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VelocityMindDashboard;
